import { CategoryTilesRow } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontCategoryTiles } from '../../../services/portalDashboardService';

/**
 * Category Tiles widget: loads the brand's categories (anonymous-safe) and
 * draws them with the shared row, each tile opening `/catalog?category=<name>`.
 */
export function CategoryTilesWidget({
	title,
	limit,
	density = 'standard',
}: {
	title?: string;
	limit?: number;
	/** Set per widget instance via `widgetConfig.density`. */
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();

	const queryKey = ['portal', 'dashboard', 'category-tiles', limit ?? null];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontCategoryTiles(token, limit),
		staleTime: 10 * 60_000,
	});

	return (
		<CategoryTilesRow
			title={title}
			categories={data?.data ?? []}
			isLoading={isLoading}
			isError={isError}
			density={density}
			onBrowseAll={() => {
				void navigate('/catalog');
			}}
			onSelectCategory={(name) => {
				// An empty value would read as "no filter", so fall back to the
				// plain catalog rather than an unfiltered-looking filter link.
				if (name === '') {
					void navigate('/catalog');
					return;
				}
				void navigate(`/catalog?category=${encodeURIComponent(name)}`);
			}}
			onLikelyStaleUrl={() => {
				void queryClient.invalidateQueries({ queryKey });
			}}
		/>
	);
}
